/**
 * AgendaAvailabilityModal — edita a janela de trabalho do card
 * "Minha disponibilidade" (tabela user_availability, migration 034).
 *
 * Uma linha por dia da semana (1 = segunda … 7 = domingo, ISO do Postgres),
 * com horário de início/fim e um intervalo opcional (almoço). O grid da Agenda
 * usa isso pra escurecer as horas fora do expediente.
 *
 * Props:
 *   availability — { timezone, dias: [...] } vindo de agGetAvailability
 *   userId       — dono da disponibilidade (null = usuário logado)
 *   onClose()    — fecha sem salvar
 *   onSaved(a)   — salvou (devolve a disponibilidade já normalizada pelo backend)
 */

import { useState } from 'react'
import Icon from '../../components/icons.jsx'
import { agSaveAvailability } from '../../db/api.js'
import '../financeiro/contas.css'
import './agenda.css'

const DIAS = [
  { dow: 1, label: 'Segunda' },
  { dow: 2, label: 'Terça' },
  { dow: 3, label: 'Quarta' },
  { dow: 4, label: 'Quinta' },
  { dow: 5, label: 'Sexta' },
  { dow: 6, label: 'Sábado' },
  { dow: 7, label: 'Domingo' },
]

export default function AgendaAvailabilityModal({ availability, userId = null, onClose, onSaved }) {
  const [dias, setDias] = useState(() =>
    DIAS.map((d) => {
      const atual = (availability?.dias || []).find((x) => Number(x.dia_semana) === d.dow)
      return {
        dia_semana: d.dow,
        disponivel: atual ? !!atual.disponivel : d.dow <= 5,
        hora_inicio: atual?.hora_inicio || '08:00',
        hora_fim: atual?.hora_fim || '18:00',
        intervalo_inicio: atual?.intervalo_inicio || '',
        intervalo_fim: atual?.intervalo_fim || '',
      }
    })
  )
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  const patch = (dow, campo, valor) => {
    setDias((prev) => prev.map((d) => (d.dia_semana === dow ? { ...d, [campo]: valor } : d)))
  }

  const salvar = async () => {
    if (saving) return
    const invalido = dias.find(
      (d) => d.disponivel && d.hora_inicio && d.hora_fim && d.hora_fim <= d.hora_inicio
    )
    if (invalido) {
      setErr('O fim do expediente precisa ser depois do início em todos os dias ativos.')
      return
    }
    const intervaloInvalido = dias.find(
      (d) => d.disponivel && d.intervalo_inicio && d.intervalo_fim && d.intervalo_fim <= d.intervalo_inicio
    )
    if (intervaloInvalido) {
      setErr('O fim do intervalo precisa ser depois do início.')
      return
    }

    setSaving(true); setErr(null)
    try {
      const salvo = await agSaveAvailability(
        {
          timezone: availability?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
          dias: dias.map((d) => ({
            dia_semana: d.dia_semana,
            disponivel: d.disponivel,
            hora_inicio: d.disponivel ? (d.hora_inicio || null) : null,
            hora_fim: d.disponivel ? (d.hora_fim || null) : null,
            intervalo_inicio: d.disponivel ? (d.intervalo_inicio || null) : null,
            intervalo_fim: d.disponivel ? (d.intervalo_fim || null) : null,
          })),
        },
        userId
      )
      onSaved?.(salvo)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div className="fc-modal-overlay" onMouseDown={onClose}>
      <div className="fc-modal ag-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <span className="fc-drawer-title">Editar disponibilidade</span>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="ag-avail-list">
            {dias.map((d) => {
              const label = DIAS.find((x) => x.dow === d.dia_semana).label
              return (
                <div key={d.dia_semana} className={`ag-avail-row${d.disponivel ? '' : ' off'}`}>
                  <label className="ag-avail-day">
                    <input
                      type="checkbox" checked={d.disponivel}
                      onChange={(e) => patch(d.dia_semana, 'disponivel', e.target.checked)}
                    />
                    <span>{label}</span>
                  </label>
                  {d.disponivel ? (
                    <div className="ag-avail-times">
                      <input
                        className="fc-input" type="time" value={d.hora_inicio || ''}
                        onChange={(e) => patch(d.dia_semana, 'hora_inicio', e.target.value)}
                      />
                      <span className="ag-time-sep">às</span>
                      <input
                        className="fc-input" type="time" value={d.hora_fim || ''}
                        onChange={(e) => patch(d.dia_semana, 'hora_fim', e.target.value)}
                      />
                      <span className="ag-time-sep ag-avail-break">intervalo</span>
                      <input
                        className="fc-input" type="time" value={d.intervalo_inicio || ''}
                        onChange={(e) => patch(d.dia_semana, 'intervalo_inicio', e.target.value)}
                      />
                      <span className="ag-time-sep">às</span>
                      <input
                        className="fc-input" type="time" value={d.intervalo_fim || ''}
                        onChange={(e) => patch(d.dia_semana, 'intervalo_fim', e.target.value)}
                      />
                    </div>
                  ) : (
                    <div className="ag-avail-off">Sem expediente</div>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="fc-modal-foot">
          <div className="ag-foot-right">
            <button className="fc-ctrl" onClick={onClose} disabled={saving}>Cancelar</button>
            <button className="fc-ctrl primary" onClick={salvar} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
